import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AcessosData, FazendaAcesso, ModuloAcesso } from '../models/auth-response.model';

/**
 * Interface para itens de menu do sidebar
 */
export interface MenuItem {
  displayName?: string;
  iconName?: string;
  route?: string;
  navCap?: string;
  children?: MenuItem[];
  disabled?: boolean;
  external?: boolean;
  modulo?: string;
  ordem?: number;
  ddType?: string; // Tipo de dropdown (para horizontal sidebar)
  fontFamily?: string; // Família de fonte customizada
  fontSize?: string; // Tamanho de fonte customizado
}

@Injectable({
  providedIn: 'root'
})
export class AcessoService {
  private readonly STORAGE_KEY = 'acessos';
  private acessosSubject = new BehaviorSubject<AcessosData | null>(null);
  public acessos$ = this.acessosSubject.asObservable();

  /**
   * Mapeamento de ícones do backend para ícones válidos do Tabler Icons
   * Ícones customizados com prefixo 'icon-' devem retornar o nome original
   */
  private readonly ICON_MAP: { [key: string]: string } = {
    'person': 'user',
    'people': 'users',
    'dashboard': 'layout-dashboard',
    'settings': 'settings',
    'folder': 'folder',
    'file': 'file',
    'home': 'home',
    'business': 'building',
    'location': 'map-pin',
    'calendar': 'calendar',
    'chart': 'chart-line',
    'truck': 'truck',
    'agriculture': 'plant',
    'inventory': 'package',
    'building-store': 'building-store',
    'adjustments': 'adjustments',
    'bug': 'bug',
    'fingerprint': 'fingerprint',
    'report': 'report',
    'users-group': 'users-group',
    'tractor': 'tractor',
    'shopping-cart': 'shopping-cart',
  };

  constructor() {
    this.loadAcessosFromStorage();
  }

  /**
   * Salva os dados de acesso no localStorage
   */
  setAcessos(acessos: AcessosData): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(acessos));
    this.acessosSubject.next(acessos);
  }

  /**
   * Carrega os dados de acesso do localStorage
   */
  private loadAcessosFromStorage(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);

    if (stored) {
      try {
        const acessos = JSON.parse(stored) as AcessosData;
        this.acessosSubject.next(acessos);
      } catch (error) {
        console.error('Erro ao carregar acessos do localStorage', error);
        this.clearAcessos();
      }
    }
  }

  /**
   * Obtém os dados de acesso atuais
   */
  getAcessos(): AcessosData | null {
    return this.acessosSubject.value;
  }

  /**
   * Limpa os dados de acesso
   */
  clearAcessos(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.acessosSubject.next(null);
  }

  /**
   * Obtém todas as fazendas acessíveis
   */
  getFazendas(): FazendaAcesso[] {
    return this.acessosSubject.value?.fazendas || [];
  }

  /**
   * Obtém todos os módulos acessíveis
   */
  getModulos(): ModuloAcesso[] {
    return this.acessosSubject.value?.modulos || [];
  }

  /**
   * Obtém módulos por plataforma
   */
  getModulosPorPlataforma(plataforma: number): ModuloAcesso[] {
    return this.getModulos().filter(m => m.ePlataformaModulo === plataforma);
  }

  /**
   * Verifica se o usuário tem acesso a um módulo específico por ID
   */
  temAcessoModulo(moduloId: number): boolean {
    const modulos = this.getModulos();
    return modulos.some(m => {
      if (m.id === moduloId) return true;
      // Verifica também nos submódulos
      return m.submodulos?.some(sub => sub.id === moduloId) || false;
    });
  }

  /**
   * Verifica se o usuário tem acesso a uma rota específica
   */
  temAcessoRota(rota: string): boolean {
    const modulos = this.getModulos();
    const rotaNormalizada = rota.toLowerCase().replace(/^\//, '');

    return modulos.some(m => {
      // Verifica rota do módulo principal
      if (m.rota && m.rota.toLowerCase().replace(/^\//, '') === rotaNormalizada) {
        return true;
      }
      // Verifica rotas dos submódulos
      return m.submodulos?.some(sub =>
        sub.rota && sub.rota.toLowerCase().replace(/^\//, '') === rotaNormalizada
      ) || false;
    });
  }

  /**
   * Obtém um módulo específico por ID
   */
  getModuloPorId(moduloId: number): ModuloAcesso | undefined {
    const modulos = this.getModulos();

    for (const modulo of modulos) {
      if (modulo.id === moduloId) return modulo;

      // Busca nos submódulos
      const submodulo = modulo.submodulos?.find(sub => sub.id === moduloId);
      if (submodulo) {
        // Retorna o submódulo como se fosse um módulo
        return {
          ...modulo,
          id: submodulo.id,
          nome: submodulo.nome,
          rota: submodulo.rota,
          icone: submodulo.icone,
          ordem: submodulo.ordem,
          submodulos: []
        };
      }
    }

    return undefined;
  }

  /**
   * Verifica se o usuário tem acesso a uma fazenda específica
   */
  temAcessoFazenda(fazendaId: number): boolean {
    return this.getFazendas().some(f => f.id === fazendaId);
  }

  /**
   * Obtém a data da última atualização dos acessos
   */
  getDataAtualizacao(): Date | null {
    const acessos = this.getAcessos();
    return acessos?.atualizadoEm ? new Date(acessos.atualizadoEm) : null;
  }

  /**
   * Mapeia ícone do backend para ícone válido do Tabler Icons
   * Ícones com prefixo 'icon-' são ícones customizados e devem ser retornados sem modificação
   */
  private mapearIcone(icone: string | null | undefined): string {
    if (!icone) return 'circle';

    // Se o ícone começa com 'icon-', é um ícone customizado (mantém original)
    if (icone.startsWith('icon-')) {
      return icone;
    }

    const iconeLowerCase = icone.toLowerCase();
    return this.ICON_MAP[iconeLowerCase] || icone;
  }

  /**
   * Constrói o menu dinâmico baseado nos módulos do usuário
   */
  construirMenu(): MenuItem[] {
    // Web deve exibir apenas módulos das plataformas Web (1) e Relatório (3);
    // módulos exclusivos de App (2) não devem aparecer no menu web.
    const modulos = this.getModulos().filter(m => m.ePlataformaModulo === 1 || m.ePlataformaModulo === 3);
    if (!modulos || modulos.length === 0) {
      return [];
    }

    const menu: MenuItem[] = [];

    // Agrupa módulos por tipo
    const grupos = modulos.filter(m => m.tipo === 'grupo');
    const modulosSemGrupo = modulos.filter(m => m.tipo === 'modulo' && (!m.submodulos || m.submodulos.length === 0));

    // Adiciona item Dashboard no início (sempre visível)
    menu.push({
      navCap: 'Início'
    });
    menu.push({
      displayName: 'Dashboard',
      iconName: 'home',
      route: '/dashboard',
      ordem: 0
    });

    // Adiciona grupos e seus submódulos
    if (grupos.length > 0 || modulosSemGrupo.length > 0) {
      menu.push({
        navCap: 'Cadastros'
      });

      // Processa grupos (módulos tipo 'grupo' com submódulos)
      grupos
        .sort((a, b) => a.ordem - b.ordem)
        .forEach(modulo => {
          const hasChildren = modulo.submodulos && modulo.submodulos.length > 0;

          const menuItem: MenuItem = {
            displayName: modulo.nome,
            iconName: this.mapearIcone(modulo.icone),
            route: modulo.rota || undefined,
            modulo: modulo.nome.toLowerCase(),
            ordem: modulo.ordem,
            ddType: hasChildren ? 'sub' : '' // 'sub' se tiver filhos
          };

          // Adiciona submódulos se existirem
          if (hasChildren) {
            menuItem.children = modulo.submodulos!
              .sort((a, b) => a.ordem - b.ordem)
              .map(submodulo => ({
                displayName: submodulo.nome,
                iconName: this.mapearIcone(submodulo.icone),
                route: submodulo.rota || undefined,
                modulo: submodulo.nome.toLowerCase(),
                ordem: submodulo.ordem,
                ddType: '' // Submódulos não têm filhos
              }));
          }

          menu.push(menuItem);
        });

      // Adiciona módulos sem grupo (tipo 'modulo' sem submódulos)
      modulosSemGrupo
        .sort((a, b) => a.ordem - b.ordem)
        .forEach(modulo => {
          menu.push({
            displayName: modulo.nome,
            iconName: this.mapearIcone(modulo.icone),
            route: modulo.rota || undefined,
            modulo: modulo.nome.toLowerCase(),
            ordem: modulo.ordem,
            ddType: '' // Módulos simples não têm dropdown
          });
        });
    }

    return menu;
  }
}
